import { CheckCircle, Target, TrendUp, Globe } from "@phosphor-icons/react"
import { Card, CardContent } from "@/components/ui/card"

export function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory via-ivory to-muted">
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-8">
              <img 
                src="/images/gallery-2.jpg" 
                alt="Football training" 
                className="rounded-lg shadow-lg max-w-full h-auto"
              />
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-navy mb-6">
              About ZOE Football Academy
            </h1>
            <p className="text-xl text-navy-muted max-w-2xl mx-auto">
              ⚽ Discover. Discipline. Develop. Deploy.
            </p>
          </div>

          <div className="space-y-16">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <Target className="text-gold" size={32} weight="duotone" />
                <h2 className="font-serif text-3xl font-semibold text-navy">Our Vision</h2>
              </div>
              <Card className="border-gold/20">
                <CardContent className="pt-6">
                  <p className="text-lg text-navy-muted leading-relaxed">
                    To discover, discipline, develop, and deploy football talent across Asia and Africa—preparing 
                    players spiritually, mentally, academically, and professionally for the global game.
                  </p>
                </CardContent>
              </Card>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="text-gold" size={32} weight="duotone" />
                <h2 className="font-serif text-3xl font-semibold text-navy">Our Mission</h2>
              </div>
              <Card className="border-gold/20">
                <CardContent className="pt-6">
                  <p className="text-lg text-navy-muted mb-6">
                    ZOE Football Academy exists to build a world‑class football development system that:
                  </p>
                  <ul className="space-y-3">
                    {[
                      "Identifies raw talent early",
                      "Trains players with elite technical and tactical standards",
                      "Instills discipline, leadership, and integrity",
                      "Creates ethical pathways to clubs, scholarships, and national teams"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 flex-shrink-0" />
                        <span className="text-navy-muted">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </section>

            <section>
              <h2 className="font-serif text-3xl font-semibold text-navy mb-6">
                The Meaning of ZOE
              </h2>
              <Card className="border-gold/20 bg-gradient-to-br from-gold/5 to-gold/10">
                <CardContent className="pt-6">
                  <p className="text-lg text-navy-muted leading-relaxed">
                    ZOE represents life that flows from the source. Our philosophy prioritizes{" "}
                    <span className="font-semibold text-navy">formation before performance</span>,{" "}
                    <span className="font-semibold text-navy">continuity over shortcuts</span>, and{" "}
                    <span className="font-semibold text-navy">character over recognition</span>.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Image 4: Single page image - intimate, environmental context */}
            <div className="aspect-[3/1] overflow-hidden rounded-lg my-12">
              <img 
                src="/images/gallery-4.jpg" 
                alt="" 
                className="w-full h-full object-cover object-top"
                aria-hidden="true"
              />
            </div>

            <section>
              <h2 className="font-serif text-3xl font-semibold text-navy mb-6">
                What Makes Us Different
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Long‑term player formation, not short‑term wins",
                  "Discipline and conduct embedded into daily training",
                  "International exposure aligned with global regulations",
                  "Strong parental engagement and player welfare focus"
                ].map((item, idx) => (
                  <Card key={idx} className="border-gold/20 hover:border-gold/40 transition-colors">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="text-gold flex-shrink-0 mt-0.5" size={20} weight="fill" />
                        <span className="text-navy-muted">{item}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <TrendUp className="text-gold" size={32} weight="duotone" />
                <h2 className="font-serif text-3xl font-semibold text-navy">Long‑Term Impact</h2>
              </div>
              <Card className="border-gold/20">
                <CardContent className="pt-6">
                  <p className="text-lg text-navy-muted mb-6">
                    Over the next decade, ZOE Football Academy aims to:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {[
                      { value: "10+", label: "Countries of operation" },
                      { value: "50,000+", label: "Young players trained" },
                      { value: "1,000+", label: "International placements" },
                      { value: "National", label: "Team contributors" }
                    ].map((stat, idx) => (
                      <div key={idx} className="text-center p-4 rounded-lg bg-gold/5">
                        <div className="font-serif text-3xl font-semibold text-navy mb-2">
                          {stat.value}
                        </div>
                        <div className="text-sm text-navy-muted">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>

            <section className="text-center py-12 px-8 rounded-lg bg-gradient-to-r from-navy via-navy-muted to-navy">
              <Globe className="mx-auto mb-6 text-gold" size={48} weight="duotone" />
              <h3 className="font-serif text-3xl font-semibold text-ivory mb-4">
                Building Disciplined Champions
              </h3>
              <p className="text-ivory/80 text-lg">
                On and off the pitch
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
